import { useContractEvent, usePublicClient } from 'wagmi';
import { formatEther, parseAbiItem } from 'viem';
import { useState, useEffect } from 'react';
import { CONTRACT_ADDRESS, CONTRACT_ABI } from '../config/contract';

function truncateAddress(address) {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export function RecentMints() {
  const [recentMints, setRecentMints] = useState([]);
  const publicClient = usePublicClient();

  const formatWholeNumber = (value) => {
    if (!value) return '0';
    const number = Number(formatEther(value));
    return Math.floor(number).toLocaleString();
  };

  // Function to fetch recent mints
  const fetchRecentMints = async () => {
    try {
      const blockNumber = await publicClient.getBlockNumber();
      // Look back more blocks but still avoid unfinalized ones
      const fromBlock = blockNumber - 1000n;
      
      const logs = await publicClient.getLogs({
        address: CONTRACT_ADDRESS,
        event: parseAbiItem('event Minted(address indexed minter, uint256 amount)'),
        fromBlock,
        toBlock: blockNumber - 1n // Only query finalized blocks
      });

      // Process logs without fetching individual blocks
      const mintsWithTimestamp = logs
        .map(log => ({
          address: log.args.minter,
          amount: log.args.amount,
          transactionHash: log.transactionHash,
          timestamp: Date.now() // Use current time as fallback
        }))
        .sort((a, b) => b.timestamp - a.timestamp) // Sort by timestamp
        .slice(0, 5); // Take most recent 5

      setRecentMints(prev => {
        // Combine new mints with existing ones
        const combined = [...mintsWithTimestamp, ...prev];
        // Remove duplicates and keep only most recent 5
        const unique = Array.from(
          new Map(combined.map(mint => [mint.transactionHash, mint])).values()
        );
        return unique
          .sort((a, b) => b.timestamp - a.timestamp)
          .slice(0, 5);
      });
    } catch (error) {
      console.error('Error fetching recent mints:', error);
    }
  };

  // Set up polling interval
  useEffect(() => {
    fetchRecentMints(); // Initial fetch
    const interval = setInterval(fetchRecentMints, 1250);
    return () => clearInterval(interval);
  }, []);

  // Listen for new mint events in real-time
  useContractEvent({
    address: CONTRACT_ADDRESS,
    abi: CONTRACT_ABI,
    eventName: 'Minted',
    listener: (log) => {
      const newMint = {
        address: log.args.minter,
        amount: log.args.amount,
        transactionHash: log.transactionHash,
        timestamp: Date.now()
      };

      setRecentMints(prev => {
        const combined = [newMint, ...prev];
        const unique = Array.from(new Map(combined.map(mint => [mint.transactionHash, mint])).values());
        return unique.slice(0, 5);
      });
    },
  });

  return (
    <div className="card recent-mints-card">
      <h2>Recent Mints</h2>
      <div className="mints-list">
        <div className="mint-header">
          <span>Minter</span>
          <span>Tx ID</span>
          <span>Amount</span>
          <span>Time</span>
        </div>
        {recentMints.length === 0 ? (
          <div className="mint-item empty">
            <span>No recent mints</span>
          </div>
        ) : (
          recentMints.map((mint) => (
            <div key={mint.transactionHash} className="mint-item">
              <a href={`https://snowtrace.io/address/${mint.address}`} target="_blank" rel="noopener noreferrer">
                {truncateAddress(mint.address)}
              </a>
              <a href={`https://snowtrace.io/tx/${mint.transactionHash}`} target="_blank" rel="noopener noreferrer">
                {truncateAddress(mint.transactionHash)}
              </a>
              <span className="mint-amount">{formatWholeNumber(mint.amount)} BMC</span>
              <span className="mint-time">{new Date(mint.timestamp).toLocaleTimeString()}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
} 